import type { GuideChapter } from '../types'
import { n } from '../types'

export const projects: GuideChapter = {
  id: 'sager',
  title: 'Sager, opgaver, kunder og kalender',
  icon: '📁',
  intro: `Sagen er det sted, alt samles: opgaver, fotos, mangler, koder til porte og kommentarer. En medarbejder ser de sager, han står på - projektledere og administratorer ser alle.`,
  sections: [
    {
      id: 'projects-list',
      title: 'Find en sag',
      icon: '🔎',
      role: 'Medarbejder',
      where: `"Sager" i menuen`,
      intro: `Sagslisten viser et kort for hver sag med kunde, adresse, ordrenummer, status og fremdrift. En servicesag står med "🔧 Servicesag" øverst på kortet.`,
      sketch: {
        device: 'desktop',
        title: 'Sager',
        blocks: [
          {
            t: 'btns',
            items: [{ text: 'Søg i sager ...', n: 1 }, { text: 'Alle statusser' }, { text: 'Alle typer', n: 2 }, { text: 'Alle kunder' }, { text: 'Ny sag', primary: true }]
          },
          {
            t: 'cols',
            left: [
              {
                t: 'card',
                n: 3,
                children: [
                  { t: 'pills', items: ['🔧 Servicesag'] },
                  { t: 'title', text: 'Batteri - eftersyn', sub: 'TC EL · Industrivej 12 · Ordre 4850-1' },
                  { t: 'pills', items: ['I gang', 'Normal'] },
                  { t: 'bar', value: 60, label: 'Fremdrift 3/5 opgaver' }
                ]
              }
            ],
            right: [
              {
                t: 'card',
                children: [
                  { t: 'title', text: 'Tavleudskiftning', sub: 'Aalborg Boligforening · Ordre 4851' },
                  { t: 'pills', items: ['Afventer'] },
                  { t: 'bar', value: 20, label: 'Fremdrift 1/5 opgaver' }
                ]
              }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Find en sag',
          steps: [
            n(1, `Skriv i søgefeltet - navn, kunde, adresse eller ordrenummer.`),
            n(2, `Filtrer evt. på status, type (sag eller service) og kunde.`),
            n(3, `Tryk på kortet for at åbne sagen.`),
            `Sæt flueben i "Vis arkiv" for at se arkiverede sager.`
          ]
        },
        {
          title: 'Find en færdig sag',
          steps: [
            `Tryk "✅ Færdige sager" øverst på Sager-siden. Her står alle sager med status "Færdig".`,
            `Søg og filtrer som på de andre sager. Tryk "Sager" for at komme tilbage til dem, der er i gang.`
          ]
        }
      ],
      tips: [
        `Færdige sager står ikke i den almindelige liste - kun under "✅ Færdige sager".`,
        `Sagens mappe i SharePoint flyttes også: inden for 5 minutter ligger den under "Sager/Færdige sager". Genåbnes sagen, flyttes mappen tilbage. Filerne på sagen virker hele tiden.`,
        `Sagens egen adresse står med fed. Den skiller sagerne ad hos en kunde med mange sager.`,
        `Kan du ikke finde en sag, står du måske ikke på den. Spørg projektlederen.`
      ]
    },
    {
      id: 'project-create',
      title: 'Opret en ny sag',
      icon: '➕',
      role: 'Projektleder',
      where: `"Sager" → "Ny sag"`,
      intro: `En sag oprettes med ordrenummeret fra Ordrestyring. Vælger du en procedure, bliver sagens opgaver oprettet med det samme.`,
      sketch: {
        device: 'desktop',
        title: 'Sager',
        blocks: [
          {
            t: 'card',
            title: 'Ny sag',
            children: [
              {
                t: 'cols',
                left: [
                  { t: 'field', label: 'Ordrenummer *', value: '4850-1', n: 1 },
                  { t: 'field', label: 'Sagsnavn *', value: 'Tavleudskiftning, opgang B' },
                  { t: 'field', label: 'Kunde', value: 'Aalborg Boligforening', n: 3 },
                  { t: 'field', label: 'Procedure (typen af sag)', value: 'Tavle', n: 4 },
                  { t: 'field', label: 'Skabelon (opretter opgaverne)', value: 'Ny tavle i parcelhus' }
                ],
                right: [
                  { t: 'field', label: 'Kundens sagsnummer', n: 2 },
                  { t: 'field', label: 'Type', value: 'Sag' },
                  { t: 'field', label: 'Hvor foregår arbejdet?', value: 'tom = hos kunden' },
                  { t: 'field', label: 'Startdato', value: '15.09.2026' }
                ]
              },
              { t: 'check', text: 'Jens Montør', checked: true, n: 5 },
              { t: 'btns', items: [{ text: 'Opret sag', primary: true, n: 6 }, { text: 'Fortryd' }] }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Opret en sag',
          steps: [
            `Tryk "Ny sag".`,
            n(1, `Skriv ordrenummeret, som det står i Ordrestyring, f.eks. 4850-1.`),
            n(2, `Skriv kundens eget sagsnummer, hvis kunden har et.`),
            n(3, `Skriv sagsnavnet og vælg kunden. Findes kunden ikke, så tryk "Ny" ved siden af.`),
            n(4, `Vælg proceduren (typen af sag) og derefter skabelonen. Har proceduren kun én skabelon, vælges den selv. Sæt en startdato - så får opgaverne deres deadlines.`),
            n(5, `Sæt flueben ved de medarbejdere, der skal på sagen.`),
            n(6, `Tryk "Opret sag".`)
          ]
        },
        {
          title: 'Tilføj enheder og rettigheder, mens sagen oprettes',
          steps: [
            `Skriv "Rettigheder (A)" - hvor mange ampere installationen har ret til, f.eks. 63. Tallet står på sagens adresse, eller på kunden, når arbejdet foregår hos kunden.`,
            `Under "Nye enheder på sagen": tryk "+ Tilføj enhed", og skriv navn, type, model og placering. Serienummeret kan vente.`,
            `Skal der fire ens batterier op, så tryk "Kopiér" - så kommer "Batteri 2", "Batteri 3" osv. med de samme oplysninger.`,
            `En ny tavle oprettes på samme måde som en enhed. Tavletegningen lægges på enheden bagefter under "Dokumenter".`,
            `Enhederne oprettes sammen med sagen, når du trykker "Opret sag".`
          ]
        },
        {
          title: 'Opret en servicesag',
          steps: [
            `Vælg "Type": "Service - hos en kunde med enheder", og vælg kunden - den er påkrævet på en servicesag.`,
            `Sæt flueben ved de af kundens enheder, servicen gælder.`,
            `Tryk "Opret servicesag". Den kan også oprettes fra "Service" → "Ny servicesag".`
          ]
        }
      ],
      tips: [
        `Ordrenummeret er påkrævet. Det samme nummer må gerne stå på flere sager.`,
        `Sagens mappe i SharePoint (under "Sager", med Billeder og Dokumenter) oprettes af sig selv få sekunder efter, at sagen er oprettet.`,
        `Lades "Kundens sagsnummer" tomt, bruges vores eget ordrenummer.`,
        `"Hvor foregår arbejdet?" udfyldes kun, når arbejdet ikke er på kundens adresse.`
      ]
    },
    {
      id: 'project-diagrams',
      title: 'Tegn et diagram',
      icon: '📐',
      role: 'Projektleder',
      where: `"Diagrammer" i menuen - eller "📐 Diagrammer" → "+ Ny tegning" på sagen`,
      intro: `Tegn enstregsdiagrammer som husets regneark - måler, afbrydere, invertere, batterier, kabler og samleskinner - ved at trække symbolerne ud på tegningen. Diagrammet hører til en sag, kan rettes igen, og "Gem som PDF i sagen" lægger det i sagens Dokumenter-mappe. Alt, der oprettes og rettes, står i sagens aktivitetslog.`,
      sketch: {
        device: 'desktop',
        title: 'Diagram',
        blocks: [
          { t: 'btns', items: [{ text: '💾 Gem', primary: true, n: 4 }, { text: '📄 Gem som PDF i sagen', n: 5 }, { text: '👁 Vis PDF' }] },
          {
            t: 'card',
            children: [
              {
                t: 'cols',
                left: [
                  { t: 'field', label: 'Overskrift', value: 'Batterianlæg', n: 1 },
                  { t: 'field', label: 'Inst nr.', value: 'står med gult, til det er udfyldt' }
                ],
                right: [
                  { t: 'field', label: 'Installationsadresse', value: 'Fittingvej 1' },
                  { t: 'field', label: 'Ekstra linje', value: 't-ret: 330kW' }
                ]
              }
            ]
          },
          {
            t: 'cols',
            left: [
              { t: 'row', text: 'Måler · Afbryder · Inverter · Batteri / kasse · Kabel · Samleskinne · Ejergrænse · Pil · Tekst · Gul markering', n: 2 }
            ],
            right: [{ t: 'card', title: 'Valgt symbol', children: [{ t: 'field', label: 'Tekst over', value: '4x150mm2 AL', n: 3 }, { t: 'btns', items: [{ text: 'Kopiér' }, { text: 'Fjern' }] }] }]
          }
        ]
      },
      flows: [
        {
          title: 'Start en ny tegning',
          steps: [
            `Tryk "+ Ny tegning", vælg sagen og giv diagrammet et navn. Findes sagen ikke endnu, så tryk "Findes sagen ikke? Opret den her" - så oprettes den med den almindelige formular og vælges bagefter.`,
            n(1, `Tryk "Start tegningen". Adressen og husets navn står allerede i hovedet - ret dem, hvis det skal være anderledes.`),
            n(2, `Træk symbolerne ud på tegningen. Du kan også bare trykke på et symbol - så kommer det midt på tegningen.`),
            n(3, `Tryk på et symbol for at skrive teksten over det. Enter giver en ny linje, f.eks. "4x150mm2 AL" og "1G95mm2 AL".`),
            n(4, `Tryk "💾 Gem". Diagrammet kan åbnes og rettes igen fra sagen eller fra "Diagrammer".`),
            n(5, `Er det færdigt, så tryk "📄 Gem som PDF i sagen". PDF'en lægges i sagens Dokumenter-mappe med installationsadressen i navnet, f.eks. "Diagram - Batterianlæg - Fittingvej 1.pdf" - en ny PDF erstatter den forrige.`)
          ]
        },
        {
          title: 'Tegn kabler',
          steps: [
            `Tryk "✏️ Tegn kabel", og træk på tegningen fra det ene punkt til det andet. Kablet bliver altid vandret eller lodret - den retning, du trækker mest i, vinder.`,
            `Starter eller slutter du tæt på et symbol eller en samleskinne, sætter enden sig fast dér.`,
            `Tryk på knappen igen ("Tegner kabler - tryk for at stoppe"), når du er færdig, så du kan flytte symboler igen.`,
            `Skal et kabel være skråt, så sæt flueben i "Fri vinkel" - eller hold Alt nede, mens du trækker.`,
            `Er et kabel blevet skævt, så tryk på det og vælg "↔ Gør vandret" eller "↕ Gør lodret".`
          ]
        },
        {
          title: 'Flyt og ret symboler',
          steps: [
            `Træk i et symbol for at flytte det - det sætter sig i gitteret.`,
            `Symbolerne sætter sig på linjen: kommer en måler, afbryder eller inverter tæt på et kabel eller et andet symbol, rettes den ind på præcis samme højde. En orange stiplet linje viser, hvad den sidder fast på.`,
            `Træk et kabels ende hen til et symbol - den sætter sig fast i symbolets tilslutning (de orange prikker). Den sætter sig også fast på en samleskinne eller et andet kabel.`,
            `Et kabel eller en samleskinne har to runde prikker i enderne, når den er valgt. Træk i dem for at gøre den længere eller dreje den - næsten vandret eller lodret rettes selv ud.`,
            `En kasse ændres i størrelse med prikken i hjørnet nederst til højre.`,
            `"Kopiér" (eller Ctrl+D) laver en kopi lige under - fire batterirækker bygges hurtigt af én.`,
            `Står to tekster oven i hinanden, så tag fat i teksten og træk den, hvor den skal stå. Den følger stadig sit symbol. "Sæt teksten tilbage" lægger den på plads igen.`
          ]
        },
        {
          title: 'Læg dokumenter på sagen',
          steps: [
            `Under "Dokumenter på sagen" til højre for tegningen: tryk "📎 Upload dokumenter" og vælg filerne.`,
            `De lægges i sagens Dokumenter-mappe sammen med diagrammets PDF, og kan åbnes ved at trykke på navnet.`
          ]
        }
      ],
      tips: [
        `Kun projektledere og opefter kan se og bruge diagrammerne.`,
        `Ctrl+Z fortryder, Delete sletter det valgte symbol, og piletasterne flytter det.`,
        `"👁 Vis PDF" viser PDF'en uden at lægge den i sagen.`,
        `Lukker du siden med ændringer, der ikke er gemt, spørger browseren først.`
      ]
    },
    {
      id: 'project-page',
      title: 'Sagssiden',
      icon: '📄',
      role: 'Medarbejder',
      where: `Tryk på en sag`,
      intro: `Øverst står kunde, adresse, ordrenummer, status og fremdrift, og du kan ringe til kontaktpersonen eller få kørselsvejledning. Lige under står "Til næste gang" - det, der mangler, før I kører derud igen. Så følger næste handling, enheder, fotos, materialer, mangler, opgaver, medarbejdere og kommentarer.`,
      sketch: {
        device: 'phone',
        title: 'Sag',
        nav: true,
        blocks: [
          {
            t: 'card',
            children: [
              { t: 'title', text: 'Tavleudskiftning, opgang B', sub: 'Aalborg Boligforening · Industrivej 12 · Ordre 4850-1' },
              { t: 'btns', items: [{ text: '🔑 Port og nøgleboks', n: 1 }, { text: '🏷️ Mærkater til sagskasser', n: 8 }, { text: 'I gang' }] },
              { t: 'bar', value: 40, label: 'Fremdrift 2 af 5 opgaver' },
              { t: 'row', text: 'Kontaktperson · 20 30 40 50', meta: 'Tryk for at ringe', n: 2 }
            ]
          },
          {
            t: 'card',
            title: '📦 Sagskasser (2)',
            n: 9,
            children: [
              { t: 'btns', items: [{ text: '🏷️ Mærkater' }, { text: '+ Tilføj kasse', n: 10 }] },
              { t: 'row', text: 'Kasse 1 · På værkstedet', meta: 'Birgitte · i går 15.02' },
              { t: 'row', text: "Kasse 2 · Kabler · I Anders' bil", meta: 'Anders · i dag 07.12' },
              { t: 'btns', items: [{ text: '🚐 Med i min bil', primary: true }, { text: '🏢 Sat af på værkstedet', primary: true }] }
            ]
          },
          {
            t: 'card',
            title: 'Til næste gang · 1 mangler',
            n: 6,
            children: [
              { t: 'field', label: 'Hvad mangler vi til næste gang?' },
              { t: 'row', text: '3 stk. 16A automatsikring', meta: 'Anders · i går' },
              { t: 'btns', items: [{ text: '✓ Bestilt · Birgitte' }, { text: 'Pakket i sagskassen', n: 7 }] }
            ]
          },
          { t: 'card', title: 'Næste handling', n: 3, children: [{ t: 'text', text: 'Ring til kunden om tavlen' }] },
          { t: 'card', title: 'Opgaver (5)', n: 4, children: [{ t: 'row', text: 'Montér ny tavle', right: 'I gang' }] },
          { t: 'card', title: 'Kommentarer (2)', n: 5, children: [{ t: 'field', label: 'Skriv en kommentar ...' }] }
        ]
      },
      flows: [
        {
          title: 'Skriv hvad der mangler til næste gang',
          steps: [
            n(6, `Skriv i "Til næste gang", hvad der mangler - f.eks. "3 stk. 16A automatsikring". Dit navn og billede står ved noten.`),
            `Sæt flueben i "Skal bestilles" og/eller "Skal med i sagskassen", og tryk "Tilføj".`,
            n(7, `Når det er bestilt, trykker du "Bestilt". Når det ligger i sagskassen, trykker du "Pakket i sagskassen". Knappen bliver grøn med dit navn og tidspunktet.`),
            `En note uden flueben lukkes med "Klaret". Det klarede foldes sammen under "Vis klarede".`
          ]
        },
        {
          title: 'Tag sagskassen med i bilen - eller sæt den af',
          steps: [
            `Tryk "+" øverst til højre og vælg "📷 Scan". Tryk "Start kamera" og hold det over QR-koden på sagskassen.`,
            `Tryk "🚐 Med i min bil" eller "🏢 Sat af på værkstedet". Er du fører af en firmabil, står bilen ved. "📷 Scan næste" tager den næste kasse.`,
            n(9, `Du kan også scanne med telefonens eget kamera - så åbner sagen med "📦 Sagskassen" fremhævet øverst og de samme to knapper.`),
            `Knapperne står også på sagen, hvis du ikke har mærkatet ved hånden. "Hvor har den været?" viser, hvem der havde den før.`
          ]
        },
        {
          title: 'Flere kasser på samme sag',
          steps: [
            n(10, `Tryk "+ Tilføj kasse" under "📦 Sagskasse" på sagen. Giv den gerne et navn, f.eks. "Kabler" - det kan også stå tomt.`),
            `Print et mærkat til den nye kasse (se nedenfor). Hver kasse har sin egen QR-kode, så den holdes styr på for sig.`,
            `"Navn" retter kassens navn. "Fjern" tager en kasse af sagen - dens mærkat virker så ikke længere. Kasse 1 kan ikke fjernes.`
          ]
        },
        {
          title: 'Print mærkat til sagskassen',
          steps: [
            n(8, `Tryk "🏷️ Mærkater til sagskasser" øverst på sagen (eller "🏷️ Mærkater" ved sagskasserne).`),
            `Har sagen flere kasser, så sæt flueben ved dem, der skal printes - typisk kun den nye.`,
            `Vælg printeren (Brother QL, 62 mm endeløs, eller ark) og tryk "Print". Mærkatet ligner værktøjets: QR-koden til venstre, og til højre ordrenummer, kassens nummer og navn og adressen.`,
            `Scan QR-koden med telefonens kamera, så åbner sagen med den kasse fremhævet.`
          ]
        },
        {
          title: 'Brug sagen ude på stedet',
          steps: [
            n(2, `Tryk på telefonnummeret for at ringe, eller på adressen for at vælge Apple Kort eller Google Maps.`),
            n(1, `Skal du gennem en port eller bruge en nøgleboks, så tryk "🔑 Port og nøgleboks".`),
            n(3, `Læs "Næste handling" - det er det, der skal ske som det næste.`),
            n(4, `Opdatér dine opgaver, når du går i gang og bliver færdig.`),
            n(5, `Skriv en kommentar, hvis noget skal huskes af andre.`)
          ]
        }
      ],
      tips: [
        `Står der "Afventer" i en orange boks, venter sagen på noget - årsagen står i boksen.`,
        `Har du trykket "Bestilt" eller "Pakket i sagskassen" ved en fejl, så tryk på det grønne mærke for at fortryde.`,
        `Kun den, der skrev noten, og projektledere kan fjerne den.`,
        `Fremdriften er farvet som på TV-skærmen: rød under 25 %, gul fra 25 % og grøn fra 90 %.`,
        `Er sagen oprettet fra en skabelon, står opgaverne under deres hovedemner. Ved hvert hovedemne står dets andel af sagen og hvor langt det er.`
      ]
    },
    {
      id: 'access-codes',
      title: 'Port og nøgleboks - koder på sagen',
      icon: '🔑',
      role: 'Medarbejder',
      where: `Sagen → "🔑 Port og nøgleboks" øverst`,
      intro: `Koder til porte og nøglebokse hos kunden står her - så mange, sagen har brug for. Koderne er krypteret og vises først, når du trykker "Vis kode". Hver gang en kode bliver vist, står det i sagens aktivitetslog, hvem der så den.`,
      sketch: {
        device: 'phone',
        title: 'Port og nøgleboks',
        nav: true,
        blocks: [
          { t: 'text', text: '← Tilbage til sagen' },
          { t: 'btns', items: [{ text: '+ Tilføj port', primary: true, n: 1 }, { text: '+ Tilføj nøgleboks', primary: true }] },
          {
            t: 'card',
            title: '🚧 Porte (1)',
            children: [
              { t: 'row', text: 'Port ved indkørslen', meta: 'Tryk # efter koden', right: 'Ret · Slet', n: 4 },
              { t: 'btns', items: [{ text: '••••••' }, { text: 'Vis kode', primary: true, n: 2 }] }
            ]
          },
          {
            t: 'card',
            title: '🔐 Nøglebokse (1)',
            children: [
              { t: 'row', text: 'Nøgleboks ved hoveddøren' },
              { t: 'code', text: '4711#', n: 3 },
              { t: 'text', text: 'Skjul · Skjules om 29 sek.' }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Tilføj en port eller nøgleboks',
          steps: [
            n(1, `Tryk "+ Tilføj port" eller "+ Tilføj nøgleboks".`),
            `Skriv hvad det er (f.eks. "Port ved indkørslen"), koden, og evt. en bemærkning om hvor den sidder.`,
            `Tryk "Gem". Koden er nu skjult.`
          ]
        },
        {
          title: 'Se en kode',
          steps: [
            n(2, `Tryk "Vis kode".`),
            n(3, `Koden står fremme i 30 sekunder og skjules så af sig selv. Tryk "Skjul" for at skjule den med det samme.`)
          ]
        },
        {
          title: 'Kunden har skiftet koden',
          steps: [n(4, `Tryk "Ret", skriv den nye kode, og tryk "Gem". Lader du kodefeltet stå tomt, beholdes den gamle.`)]
        }
      ],
      tips: [
        `Skriv aldrig en kode i en kommentar, en note eller på en opgave - kun her er den krypteret.`,
        `Alle, der kan se sagen, kan se og rette koderne.`,
        `Koderne slettes sammen med sagen.`
      ]
    },
    {
      id: 'tasks',
      title: 'Opgaver - status, fremdrift, udført arbejde og mangler',
      icon: '✅',
      role: 'Medarbejder',
      where: `Sagen → "Opgaver"`,
      intro: `Er du ansvarlig for en opgave, sætter du den i gang og markerer den færdig. Bliver du ikke færdig, skriver du, hvor langt du er, hvad du har lavet, og hvad der mangler.`,
      sketch: {
        device: 'phone',
        title: 'Sag · Opgaver',
        nav: true,
        blocks: [
          {
            t: 'card',
            children: [
              { t: 'title', text: 'Montér ny tavle', sub: 'Jens Montør · Deadline 18.09' },
              { t: 'btns', items: [{ text: 'Sæt i gang', n: 1 }, { text: 'Marker færdig', primary: true, n: 2 }, { text: 'Sæt til afventer', n: 3 }] },
              { t: 'btn', text: 'Fremdrift, udført og mangler', n: 4 },
              { t: 'bar', value: 50, label: 'Hvor langt er opgaven? 50 %' },
              { t: 'field', label: 'Hvad har du lavet på opgaven i dag?', n: 5 },
              { t: 'row', text: 'HPFI-afbryder', meta: 'Bestilt hos grossisten · ventes 20.09', right: 'Klar', n: 6 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Den almindelige dag',
          steps: [n(1, `Tryk "Sæt i gang", når du begynder.`), n(2, `Tryk "Marker færdig", når opgaven er lavet.`)]
        },
        {
          title: 'En opgave hos dit team',
          steps: [
            `Står der et team på opgaven (👷 og profilbillederne af holdet), kan alle på teamet se den under "Mine opgaver" på forsiden.`,
            `Tryk "Tag opgaven", når du går i gang med den. Så er du ansvarlig, og den forsvinder fra de andres "Mine opgaver".`,
            `Er en anden kommet først, står der, at opgaven allerede er taget.`
          ]
        },
        {
          title: 'Du blev ikke færdig',
          steps: [
            n(4, `Tryk "Fremdrift, udført og mangler".`),
            `Sæt procenten, og tryk "Gem". En opgave på 50 % tæller halvt i sagens fremdrift.`,
            n(5, `Skriv, hvad du har lavet, og tryk "Skriv i loggen".`),
            n(6, `Skriv det, der mangler, og tryk "Tilføj". Tryk "Bestilt", når det er bestilt, og "Klar", når det er kommet.`)
          ]
        },
        {
          title: 'Opgaven venter på noget',
          steps: [n(3, `Tryk "Sæt til afventer", skriv hvad den venter på, og tryk "Gem".`)]
        }
      ],
      tips: [
        `Kun opgavens ansvarlige og medansvarlige - og projektledere - kan ændre status.`,
        `Loggen over udført arbejde kan ikke rettes bagefter. Den skal kunne læses, som den blev skrevet.`,
        `Mangellisten her er det, der står i vejen for at blive færdig - ikke kundens mangler ved afleveringen.`
      ]
    },
    {
      id: 'invoicing',
      title: 'Til fakturering - sager på 100 %',
      icon: '💰',
      role: 'Projektleder',
      where: `"Til fakturering" i menuen - kun for kontoret`,
      intro: `Når alle opgaver på en sag er færdige (100 %), skifter sagen selv til "Klar til faktura" og kommer på kontorets liste. Et rødt tal i menuen og en stribe øverst viser, hvor mange der venter - ligesom ved kvitteringerne. Siden ses kun af Kontor og administratorer.`,
      sketch: {
        device: 'desktop',
        title: 'Til fakturering',
        blocks: [
          { t: 'text', text: '💰 2 sager er klar til faktura · Se sagerne', n: 1 },
          {
            t: 'card',
            title: '💰 Klar til faktura (2)',
            children: [
              { t: 'row', text: 'Ordre 4850-1 · Tavleudskiftning, opgang B', meta: 'Aalborg Boligforening · klar for 2 dage siden', n: 2 },
              { t: 'btns', items: [{ text: '✓ Faktureret', primary: true, n: 3 }] }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Fakturér en færdig sag',
          steps: [
            n(1, `Tryk "Se sagerne" i striben øverst, eller "Til fakturering" i menuen.`),
            n(2, `Den sag, der har ventet længst, står øverst. Tryk på navnet for at åbne sagen.`),
            n(3, `Når fakturaen er sendt fra Ordrestyring, så tryk "✓ Faktureret". Sagen står så som "Færdig", forsvinder fra listen og findes under "✅ Færdige sager".`)
          ]
        }
      ],
      tips: [
        `Genåbnes en opgave på en sag, der er klar til faktura, går sagen tilbage til "I gang" og forsvinder fra listen, til den igen er på 100 %.`,
        `En sag, der allerede står som "Færdig" eller "Annulleret", flyttes aldrig af sig selv.`
      ]
    },
    {
      id: 'project-edit',
      title: 'Ret sagen og styr opgaverne',
      icon: '✏️',
      role: 'Projektleder',
      where: `Sagssiden`,
      intro: `Som projektleder kan du rette alle sager: skifte kunde, ordrenummer, sagsnummer, projektleder og type, sætte næste handling og status, oprette opgaver, sætte folk på - og arkivere sagen, når den er afsluttet.`,
      sketch: {
        device: 'desktop',
        title: 'Sag',
        blocks: [
          {
            t: 'card',
            children: [
              { t: 'title', text: 'Tavleudskiftning, opgang B', sub: 'Aalborg Boligforening' },
              { t: 'btns', items: [{ text: 'Skift kunde', n: 1 }, { text: 'Ordre 4850-1 · Ret' }, { text: 'Projektleder: Pia · Skift' }, { text: '📝 Aktivitetslog' }, { text: 'Arkivér', n: 7 }] }
            ]
          },
          { t: 'card', title: 'Næste handling', children: [{ t: 'btn', text: 'Rediger', n: 2 }] },
          {
            t: 'card',
            title: 'Opgaver (5)',
            children: [
              { t: 'btns', items: [{ text: 'Tilføj fra procedure ...', n: 3 }, { text: 'Ny opgave', primary: true, n: 4 }] },
              { t: 'row', text: 'Montér ny tavle', meta: 'Jens Montør', right: 'Skift', n: 5 }
            ]
          },
          { t: 'card', title: 'Medarbejdere på sagen', children: [{ t: 'btn', text: 'Rediger', n: 6 }] }
        ]
      },
      flows: [
        {
          title: 'Sæt næste handling og status',
          steps: [
            n(2, `Tryk "Rediger" under "Næste handling".`),
            `Skriv hvad der skal ske, vælg ansvarlig og frist, og skift evt. "Projektstatus".`,
            `Sagen skifter selv fra "Ikke startet" til "I gang", når den første opgave sættes i gang eller får udført arbejde - det behøver du ikke gøre i hånden.`,
            `Sættes sagen på afventer, skal der skrives en årsag. Tryk "Gem".`
          ]
        },
        {
          title: 'Opgaver og folk',
          steps: [
            n(3, `Tilføj en procedures opgaver med "Tilføj fra procedure ...".`),
            n(4, `Eller opret én opgave med "Ny opgave".`),
            n(5, `Tryk "Sæt på" ved en opgave for at sætte en medarbejder på - eller "Skift" for at sætte en anden på. Kræver Projektleder.`),
            n(6, `Sæt flueben ved folk under "Medarbejdere på sagen" - så kan de se sagen.`)
          ]
        },
        {
          title: 'Arkivér en afsluttet sag',
          steps: [
            n(7, `Tryk "Arkivér" øverst på sagen, og bekræft. Intet slettes.`),
            `Sagen forsvinder fra sagslisten og står med mærket "Arkiveret" under "Vis arkiv".`,
            `Skal den tilbage, så åbn den under "Vis arkiv" og tryk "Gendan fra arkiv".`
          ]
        },
        {
          title: 'Kunden eller ordrenummeret er forkert',
          steps: [n(1, `Tryk "Skift kunde" eller "Ret" ved ordrenummeret, ret det, og tryk "Gem".`)]
        }
      ],
      tips: [
        `En servicesag med enheder kan ikke skifte kunde, før enhederne er fjernet - de står hos kunden.`,
        `Et ordrenummer kan rettes, men ikke fjernes.`
      ]
    },
    {
      id: 'files',
      title: 'Fotos og dokumenter på sagen',
      icon: '📷',
      role: 'Medarbejder',
      module: 'files',
      where: `Sagen → "Dokumentation"`,
      intro: `Før- og efterbilleder, tavlebilleder, kvitteringer og tegninger. Filerne ligger i sagens mappe i SharePoint - ikke på telefonen.`,
      sketch: {
        device: 'phone',
        title: 'Sag · Dokumentation',
        nav: true,
        blocks: [
          { t: 'pills', items: ['Alle', 'Før', 'Efter', 'Tavle', 'Kvittering', 'Andet'], n: 1 },
          { t: 'btns', items: [{ text: 'Tag billede', primary: true, n: 2 }, { text: 'Vælg fil' }] },
          { t: 'cols', left: [{ t: 'photo', label: 'Før', n: 3 }], right: [{ t: 'photo', label: 'Efter' }] }
        ]
      },
      flows: [
        {
          title: 'Tag et billede',
          steps: [
            n(1, `Vælg kategori, f.eks. "Før".`),
            n(2, `Tryk "Tag billede". Bagkameraet åbner.`),
            n(3, `Tryk på billedet bagefter, skriv en billedtekst, og tryk "Gem".`)
          ]
        }
      ],
      tips: [
        `Den, der har lagt en fil op, og administratorer kan slette den.`,
        `Slettede filer kan gendannes fra SharePoints papirkurv i 93 dage.`
      ]
    },
    {
      id: 'defects',
      title: 'Fejl og mangler ved aflevering',
      icon: '🧾',
      role: 'Medarbejder',
      module: 'defects',
      where: `Sagen → "Fejl og mangler"`,
      intro: `Det, kunden eller tilsynet påpeger ved afleveringen. Alle på sagen kan skrive en mangel op og markere den udbedret - med foto.`,
      sketch: {
        device: 'phone',
        title: 'Sag · Fejl og mangler',
        nav: true,
        blocks: [
          { t: 'btn', text: 'Skriv mangel op', primary: true, n: 1 },
          { t: 'row', text: '1 · Stikkontakt sidder skævt', meta: 'Køkken · Jens · Frist 20.09', right: 'Udbedret', n: 2 },
          { t: 'row', text: '2 · Manglende mærkning af gruppe 12', meta: 'Ikke fordelt' },
          { t: 'text', text: 'Vis lukkede' }
        ]
      },
      flows: [
        {
          title: 'Skriv en mangel op',
          steps: [
            n(1, `Tryk "Skriv mangel op".`),
            `Skriv hvad og hvor, tag et foto, vælg hvem der retter det, og sæt en frist.`,
            `Tryk "Gem".`
          ]
        },
        {
          title: 'Manglen er rettet',
          steps: [n(2, `Tryk "Udbedret", tag et foto af udbedringen, og tryk "Markér som udbedret".`)]
        }
      ],
      tips: [
        `Kun en projektleder kan "Afvis" et punkt (med begrundelse) eller slette det.`,
        `Alle punkter kommer med i afleveringsmappen - også de åbne.`
      ]
    },
    {
      id: 'handover',
      title: 'Afleveringsmappe og materialer',
      icon: '📦',
      role: 'Medarbejder',
      where: `Sagen → "Afleveringsmappe" og "Materialer"`,
      intro: `"Hent afleveringsmappe" laver en PDF med sagens oplysninger, udført arbejde, mangellisten og fotos. Under "Materialer" står det, der er scannet ud på sagen.`,
      flows: [
        { title: 'Lav afleveringsmappen', steps: [`Tryk "Hent afleveringsmappe". PDF'en hentes ned.`] },
        {
          title: 'Materialer på sagen',
          steps: [`Tryk "Scan flere" under "Materialer" for at scanne varer ud på sagen - se "Scan ind og ud".`]
        }
      ],
      tips: [`Kortet "Materialer" vises først, når der er scannet noget på sagen.`]
    },
    {
      id: 'self-check',
      title: 'Egenkontrol',
      icon: '✅',
      role: 'Medarbejder',
      module: 'self_checks',
      where: `Sagen → "Egenkontrol" → "+ Ny egenkontrol"`,
      intro: `Udfyld egenkontrollen på telefonen, når installationen er færdig: visuel kontrol og målinger pr. gruppe. Når den afsluttes, lægges en PDF i sagens Dokumenter-mappe i SharePoint - derfra vedhæftes den i jeres KLS-system.`,
      sketch: {
        device: 'phone',
        title: 'Egenkontrol',
        nav: true,
        blocks: [
          { t: 'field', label: 'Måleinstrument', value: 'Fluke 1664 (123)', n: 1 },
          { t: 'btns', items: [{ text: 'I orden', primary: true, n: 2 }, { text: 'Ikke i orden' }, { text: 'Ikke relevant' }] },
          { t: 'row', text: 'Gr. 3 - stik køkken · B13', meta: 'PE 0,12 · Iso 500', n: 3 },
          { t: 'btn', text: 'Afslut og læg i SharePoint', primary: true, n: 4 }
        ]
      },
      flows: [
        {
          title: 'Udfyld',
          steps: [
            n(1, `Vælg det måleinstrument, du har målt med. Er kalibreringen udløbet, kan egenkontrollen ikke afsluttes.`),
            n(2, `Svar på hvert punkt i den visuelle kontrol. Ved "Ikke i orden" skriver du hvad og hvad der gøres ved det.`),
            n(3, `Skriv en gruppe pr. kreds med beskyttelse og målinger, og sæt flueben ved "Gruppen er i orden". Komma eller punktum - begge dele virker.`),
            n(4, `Tryk "Gem" undervejs. Tryk "Afslut og læg i SharePoint", når alt er udfyldt. Så låses den.`)
          ]
        }
      ],
      tips: [
        `Systemet vurderer ikke måleværdierne - det gør du efter de gældende regler.`,
        `"Hent PDF" viser PDF'en - også mens den er en kladde.`,
        `Afslutter man en sag uden en afsluttet egenkontrol, spørger systemet, om man er sikker. Det spærrer ikke.`,
        `Skal noget rettes efter afslutning, så lav en ny egenkontrol. Kontoret retter punkterne under kortet.`
      ]
    },
    {
      id: 'comments',
      title: 'Kommentarer og @navn',
      icon: '💬',
      role: 'Medarbejder',
      where: `Sagen → "Kommentarer", og på hver opgave`,
      intro: `Skriv til kollegerne på sagen. Skriver du @ og et navn, får kollegaen besked.`,
      flows: [
        {
          title: 'Skriv en kommentar',
          steps: [`Skriv i feltet - brug @navn for at nævne en kollega.`, `Tryk "Send".`]
        }
      ]
    },
    {
      id: 'service-project',
      title: 'Servicesag med enheder',
      icon: '🔧',
      role: 'Projektleder',
      module: 'service',
      where: `"Service" → "Ny servicesag", eller en servicesag → "Enheder på servicesagen"`,
      intro: `På en servicesag vælger du, hvilke af kundens enheder servicen gælder. Montøren trykker på enheden og starter servicen derfra.`,
      flows: [
        {
          title: 'Opret en servicesag fra Service',
          steps: [
            `Gå til "Service", og tryk "Ny servicesag" øverst.`,
            `Udfyld ordrenummer og sagsnavn, og vælg kunden.`,
            `Sæt flueben ved de af kundens enheder, servicen gælder.`,
            `Tryk "Opret servicesag". Du kommer direkte ind på sagen.`
          ]
        },
        {
          title: 'Vælg enheder',
          steps: [
            `Tryk "Vælg enheder".`,
            `Sæt flueben ved kundens enheder, og tryk "Gem".`,
            `Montøren trykker på en enhed og vælger "Start service".`
          ]
        }
      ],
      tips: [`Sagen skal have en kunde først - enhederne hører til kunden.`]
    },
    {
      id: 'activity-log',
      title: 'Aktivitetslog - hvem har gjort hvad',
      icon: '📝',
      role: 'Projektleder',
      where: `Sagen → "📝 Aktivitetslog" øverst`,
      intro: `Alt, der er sket på sagen, med navn og tidspunkt: statusskift, lukkede opgaver, planlægning, filer, mangler - og hvem der har set en kode til en port.`,
      sketch: {
        device: 'desktop',
        title: 'Aktivitetslog · Tavleudskiftning',
        blocks: [
          { t: 'btns', items: [{ text: 'Alt', primary: true }, { text: 'Statusskift', n: 1 }, { text: 'Opgaver', n: 2 }] },
          { t: 'text', text: '13.09.2026' },
          { t: 'row', text: 'Jens Montør ændrede "Montér ny tavle" fra "I gang" til "Færdig"', right: '14.32', n: 3 },
          { t: 'row', text: 'Jens Montør viste koden til porten "Port ved indkørslen"', right: '07.05' },
          { t: 'row', text: 'Pia Projektleder planlagde Jens Montør på sagen', right: '06.40' }
        ]
      },
      flows: [
        {
          title: 'Hvem lukkede opgaven?',
          steps: [
            `Tryk "📝 Aktivitetslog" øverst på sagen.`,
            n(1, `Tryk "Statusskift" for kun at se skift af status.`),
            n(3, `Linjen siger hvem, hvad og hvornår.`),
            n(2, `"Opgaver" viser alt om opgaverne.`)
          ]
        }
      ],
      tips: [`Loggen kan ikke rettes. Medarbejdere kan ikke se den.`]
    },
    {
      id: 'project-delete',
      title: 'Slet en sag',
      icon: '🗑️',
      role: 'Superadmin',
      where: `Nederst på sagen → "Slet sagen permanent"`,
      intro: `Kun en Superadmin kan slette en sag. Bekræftelsen siger præcis, hvad der forsvinder med den, og hvad der blot mister koblingen.`,
      flows: [{ title: 'Slet', steps: [`Tryk "Slet sagen permanent".`, `Læs listen, og bekræft.`] }],
      tips: [
        `Opgaver, kommentarer, kalenderposter, mangler, koder og fillisten slettes. Det kan ikke fortrydes.`,
        `Er det en rigtig sag, så tryk "Arkivér" i stedet - så kan den gendannes.`
      ]
    },
    {
      id: 'customers',
      title: 'Kunder',
      icon: '🏢',
      role: 'Medarbejder',
      where: `"Kunder" i menuen, eller tryk på kundens navn på en sag`,
      intro: `Kundekortet viser kontaktperson, telefon, e-mail, adresse, rettigheder i ampere, alle kundens sager og alle kundens enheder samlet pr. adresse.`,
      flows: [
        { title: 'Find en kunde', steps: [`Søg på navn, kontakt, adresse, postnummer, by eller telefon.`, `Tryk på kunden for at se kortet, sagerne og enhederne.`] },
        { title: 'Hvad står der på en adresse?', steps: [`Åbn "Enheder", og søg på kunden eller adressen.`, `Enhederne står samlet under hver adresse.`] }
      ]
    },
    {
      id: 'customers-manage',
      title: 'Opret og ret kunder',
      icon: '🏗️',
      role: 'Projektleder',
      where: `"Kunder" → "Ny kunde" eller "Rediger"`,
      intro: `Projektledere opretter og retter kunder. En kunde, man ikke længere arbejder for, sættes til inaktiv.`,
      flows: [
        { title: 'Opret', steps: [`Tryk "Ny kunde", udfyld, og tryk "Gem kunde".`] },
        { title: 'Deaktivér', steps: [`Tryk "Rediger", sæt "Status" til "Inaktiv", og tryk "Gem kunde".`] }
      ],
      tips: [
        `Adressen bruges på kortet - medmindre sagen har sit eget arbejdssted.`,
        `"Rettigheder (A)" er hvad installationen på kundens adresse har ret til hos netselskabet.`,
        `Inaktive kunder står først i listen, når "Vis inaktive" er sat.`
      ]
    },
    {
      id: 'customers-delete',
      title: 'Slet en kunde',
      icon: '🗑️',
      role: 'Superadmin',
      where: `Kundekortet eller "Rediger" → "Slet kunden permanent"`,
      intro: `Kun kunder uden sager, enheder og abonnementer kan slettes. Bekræftelsen siger, hvad der står i vejen.`,
      flows: [{ title: 'Slet', steps: [`Tryk "Slet kunden permanent", og bekræft.`] }]
    },
    {
      id: 'calendar',
      title: 'Kalenderen',
      icon: '📅',
      role: 'Medarbejder',
      where: `"Kalender" i menuen`,
      intro: `Kalenderen viser, hvem der er hvor - i dag, i ugen, eller pr. sag. Du ser hele holdet og kan planlægge dig selv.`,
      sketch: {
        device: 'desktop',
        title: 'Kalender',
        blocks: [
          {
            t: 'btns',
            items: [{ text: '‹' }, { text: 'I dag' }, { text: '›' }, { text: 'Dag', primary: true, n: 1 }, { text: 'Uge' }, { text: 'Sag' }, { text: 'Planlæg', primary: true, n: 2 }]
          },
          { t: 'row', text: 'Jens Montør', meta: '20 30 40 50 · 1 planlagt', right: 'Tavle - Industrivej 12 · 07-15' },
          { t: 'row', text: 'Mikkel Hansen', meta: 'Ferie', right: '+ Planlæg på sag', n: 3 }
        ]
      },
      flows: [
        {
          title: 'Se din dag og uge',
          steps: [n(1, `Vælg "Dag" eller "Uge". Pilene skifter dag eller uge.`)]
        },
        {
          title: 'Planlæg dig selv på en sag',
          steps: [
            n(2, `Tryk "Planlæg".`),
            `Vælg sag, evt. opgave, dato og klokkeslæt.`,
            `Tryk "Gem planlægning".`
          ]
        }
      ],
      tips: [
        `Stiplede poster er opgaver med deadline og næste handlinger - de flyttes på sagen, ikke i kalenderen.`,
        `Som medarbejder kan du kun planlægge og flytte dig selv.`
      ]
    },
    {
      id: 'calendar-plan',
      title: 'Planlæg holdet',
      icon: '👷',
      role: 'Projektleder',
      where: `"Kalender"`,
      intro: `Projektledere planlægger alle - flere på én gang, flere dage i træk - og kan kopiere en hel uge.`,
      flows: [
        {
          title: 'Sæt folk på en sag',
          steps: [
            `Tryk "Planlæg" eller "+ Planlæg på sag" ud for en medarbejder.`,
            `Sæt flueben ved folkene, vælg sag, dato og evt. "Gentag hverdage til og med".`,
            `Tryk "Planlæg N medarbejdere".`
          ]
        },
        { title: 'Flyt en post', steps: [`I "Uge" trækker du posten til en anden person eller dag. På telefonen: tryk ✎.`] },
        {
          title: 'Kopiér sidste uge',
          steps: [`Vælg "Uge" → "Kopiér uge".`, `Vælg fra- og til-uge, og tryk "Kopiér ugen".`]
        },
        { title: 'Udskriv dagsplanen', steps: [`Vælg "Dag" → "Udskriv dagsplan".`] }
      ],
      tips: [
        `Fravær spærrer ikke - det står i cellen, og det skrives i loggen, hvis nogen planlægges oven i.`,
        `"Kopiér uge" springer poster over, der allerede står i den nye uge.`
      ]
    },
    {
      id: 'map',
      title: 'Kortet',
      icon: '🗺️',
      role: 'Medarbejder',
      module: 'map',
      where: `"Kort" i menuen`,
      intro: `Alle åbne sager på et kort - eller alle kunder og enheder. Rød nål betyder overskredet frist, og enheder har stiplet kant.`,
      flows: [
        {
          title: 'Find sager i nærheden',
          steps: [`Tryk på en nål.`, `Tryk på navnet for at åbne sagen.`, `Sæt flueben i "Vis service der forfalder" for at se enhederne.`]
        },
        {
          title: 'Se alle kunder og enheder',
          steps: [
            `Vælg "Kunder og enheder" i listen øverst.`,
            `Hver kunde er en nål, og der står, hvor mange enheder der er på adressen. En enhed med sin egen adresse har sin egen nål.`,
            `Tryk på navnet for at åbne kunden eller enheden.`
          ]
        }
      ],
      tips: [`Mangler en sag, har kunden ingen adresse. Ret adressen - punktet kommer af sig selv, typisk inden for et kvarter.`]
    }
  ]
}
